import React, { Component } from 'react';
import { connect } from 'react-redux';
import { mapValues, pick, isEmpty, isNil, has } from 'lodash';
import { ErrorBoundary } from 'react-error-boundary';
import {
  selectPipelineItem,
  deselectAllPipelineItems,
  setDetailViewCard,
  loadFlowMetrics,
} from '../../actions';
import { getConnectionTypes } from '../../reducers/selectors';
import { GET_PIPELINES_REQUEST } from '../../constants/actionTypes';
import SourcePipeline from './SourcePipeline';
import ErrorFallback from '../elements/errors/ErrorFallback';
import AnalyticsUtils from '../../lib/AnalyticsUtils';
import { getResourceEntity } from '../../lib/PipelineUtils';
import { filterWebhookResponseSourcesFromItems } from './FlowNodes/utils';

/**
 * Validates if a branch is still valid for the current flow context.
 * A branch is considered stale/invalid if:
 * 1. Any of its items don't exist in the current flowNodes
 * 2. The root node (first item) doesn't match the expected rootKey
 * 3. The root flow node doesn't exist in flowNodes
 *
 * This prevents showing stale pipeline items from previous flows when
 * navigating between flows that share the same dataset (same origin_node_id).
 *
 * @param {Object} branch - The branch to validate { items: [], edges: [] }
 * @param {string|number} rootKey - The expected root node key (origin_node_id)
 * @param {Object} flowNodes - Current flowNodes map from entities
 * @returns {boolean} - true if branch is valid, false otherwise
 */
function isBranchValid(branch, rootKey, flowNodes) {
  if (!branch || isEmpty(branch.items)) return true; // empty branch is always valid

  if (isNil(rootKey)) return false;

  const rootNode = flowNodes[rootKey]; // if root node exists in flowNodes
  if (!rootNode) return false;

  // Validate that the root node (first item) matches the expected rootKey
  // The branch.items array should start with the root node
  const branchRootKey = branch.items[0];
  if (branchRootKey !== rootKey) {
    return false;
  }

  // Validate that all branch items exist in current flowNodes
  // This ensures we don't use branches with items from stale/removed flows
  const allItemsExist = branch.items.every((itemKey) => has(flowNodes, itemKey));
  if (!allItemsExist) {
    return false;
  }

  return true;
}

const mapStateToProps = (state, ownProps) => {
  const {
    entities,
    app: {
      listing: { aclQueryMode },
    },
    paging,
    pipelines: {
      branches,
      edges,
      selected,
      treeHeights,
      longestDataSetChainLengths,
      lastUpdated: lastLoaded,
    },
    request,
  } = state;
  const { flowNodes } = entities;
  const { dataSource } = ownProps;

  // filter pipelines to include only the branch originating from selected
  // data source
  const rootKey = dataSource && dataSource.origin_node_id;
  const candidateBranch =
    !isNil(rootKey) && has(branches, rootKey) ? branches[rootKey] : { items: [], edges: [] };

  /**
   * @Why? We need to validate the branch integrity to prevent showing stale data from previous flows
   * @How? We validate the branch integrity by checking if the branch is valid for the current flow context
   */
  const branch = isBranchValid(candidateBranch, rootKey, flowNodes)
    ? candidateBranch
    : { items: [], edges: [] };

  const rawPipelineItems = mapValues(pick(flowNodes, branch.items), (flowNode) =>
    getResourceEntity(flowNode, entities),
  );
  const pipelineItems = filterWebhookResponseSourcesFromItems(rawPipelineItems);
  const pipelineEdges = pick(edges, branch.edges);
  const selectedItem = selected;
  const isLoadingPipeline = !!request[GET_PIPELINES_REQUEST];

  const flow = flowNodes[ownProps.dataSource.origin_node_id];

  return {
    flow,
    dataSource, // Pass dataSource through so SourcePipeline can use it as dependency
    entities,
    paging,
    aclQueryMode,
    pipelineItems,
    pipelineEdges,
    selectedItem,
    branches,
    treeHeights,
    longestDataSetChainLengths,
    connectionTypes: getConnectionTypes(state),
    lastLoaded,
    isLoadingPipeline,
  };
};

const SourcePipeLineWithRedux = connect(mapStateToProps, {
  selectPipelineItem,
  deselectAllPipelineItems,
  setDetailViewCard,
  loadFlowMetrics,
})(SourcePipeline);

export default class SourcePipelineWithErrorBoundary extends Component {
  render() {
    const { onRootRef } = this.props;
    return (
      <ErrorBoundary
        fallbackRender={(errorProps) => (
          <div className="view-source-pipeline" style={{ height: 300 }} ref={onRootRef}>
            <div className="pipeline-container">
              <div className="pipeline-info incomplete-pipeline">
                <ErrorFallback {...errorProps} />
              </div>
            </div>
          </div>
        )}
        onError={(error, info) => {
          AnalyticsUtils.reportErrorToSentry(error, info);
        }}
      >
        <SourcePipeLineWithRedux {...this.props} />
      </ErrorBoundary>
    );
  }
}
